import React from "react";
import cn from "./helpers/cn";

export interface CheckboxProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange" | "size"> {
  color?: "primary" | "secondary" | "default";
  size?: "small" | "medium";
  indeterminate?: boolean;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
}

const sizeMap = {
  small: { box: "w-4 h-4", icon: "w-3 h-3", wrapperPad: "p-1.5" },
  medium: { box: "w-5 h-5", icon: "w-3.5 h-3.5", wrapperPad: "p-2" },
} as const;

const colorCheckedMap = {
  primary: "peer-checked:text-primary peer-checked:border-primary peer-checked:bg-primary peer-checked:[&>svg]:opacity-100",
  secondary: "peer-checked:text-secondary peer-checked:border-secondary peer-checked:bg-secondary peer-checked:[&>svg]:opacity-100",
  default: "peer-checked:text-slate-600 peer-checked:border-slate-600 peer-checked:bg-slate-600 peer-checked:[&>svg]:opacity-100",
} as const;

const colorIndeterminateMap = {
  primary: "text-primary border-primary bg-primary",
  secondary: "text-secondary border-secondary bg-secondary",
  default: "text-slate-600 border-slate-600 bg-slate-600",
} as const;

const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>((props, ref) => {
  const {
    color = "secondary",
    size = "medium",
    indeterminate = false,
    className,
    disabled,
    checked,
    onChange,
    inputProps,
    ...other
  } = props;

  const inputRef = React.useRef<HTMLInputElement | null>(null);

  // Espelha a propriedade DOM `indeterminate` (não há atributo HTML correspondente).
  React.useEffect(() => {
    if (inputRef.current) {
      inputRef.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  const setRefs = (node: HTMLInputElement | null) => {
    inputRef.current = node;
    if (typeof ref === "function") ref(node);
    else if (ref) (ref as React.MutableRefObject<HTMLInputElement | null>).current = node;
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange?.(event, event.target.checked);
  };

  const sz = sizeMap[size];

  return (
    <span
      className={cn(
        "relative inline-flex items-center justify-center rounded-full transition-colors group",
        sz.wrapperPad,
        !disabled && "cursor-pointer hover:bg-black/5",
        disabled && "cursor-default opacity-50",
        className
      )}
    >
      <input
        type="checkbox"
        ref={setRefs}
        checked={checked}
        disabled={disabled}
        onChange={handleChange}
        className="peer absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-default z-10"
        {...inputProps}
        {...other}
      />

      {/* Caixa do checkbox: borda visível quando vazio, preenchida quando marcado/indeterminado */}
      <span
        className={cn(
          "relative flex items-center justify-center rounded-sm border-2 border-slate-500 transition-colors",
          sz.box,
          "peer-focus-visible:ring-2 peer-focus-visible:ring-offset-2 peer-focus-visible:ring-primary",
          indeterminate ? colorIndeterminateMap[color] : colorCheckedMap[color]
        )}
      >
        {/* Ícone de check: sempre montado, visibilidade pelo estado real do input */}
        {!indeterminate && (
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="white"
            strokeWidth="4"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={cn(sz.icon, "opacity-0 transition-opacity")}
            aria-hidden="true"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
        )}

        {/* Barra horizontal para indeterminate */}
        {indeterminate && (
          <span className={cn("block bg-white rounded-sm h-0.5 w-2/3")} aria-hidden="true" />
        )}
      </span>
    </span>
  );
});

Checkbox.displayName = "Checkbox";

export default Checkbox;
